import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import axios from '../../axios/axiosconf';
import Header from '../general/Header';
import Aside from '../general/Aside';
import Footer from '../general/Footer';
import Loading from '../general/Loading';
import ModalRipsExito from './ModalRipsExito';
import ModalConfigurarSispro from './ModalConfigurarSispro';
import RegistroRipsSinFev from './RegistroRipsSinFev';
import RegistroRipsFev from './RegistroRipsFev';
import Unauthorized from '../general/Unauthorized';
import { useClientPagination } from '../reportes/useClientPagination';
import ReportPaginationFooter from '../reportes/ReportPaginationFooter';
import { getEtiquetaFactura } from '../../../utils/etiquetaFactura';
import { enviarRipsASispro, ripsYaEnviadoSispro, usuarioPuedeEnviarSispro } from '../../../utils/ripsSisproEnvio';
import { pacienteTieneDatosCompletosParaFactura } from '../../../utils/busquedaPacienteFacturacion';
import { formatDateOnly, toInputDateFromLocalDate } from '../superadmin/superadminDateUtils';

function ListadoRips() {
    const [rips, setRips] = useState([]);
    const [loading, setLoading] = useState(true);
    const [hasPermission, setHasPermission] = useState(null);
    const [fechaInicio, setFechaInicio] = useState('');
    const [fechaFin, setFechaFin] = useState('');
    const [filtroTipo, setFiltroTipo] = useState('');
    const [filtroProfesionalId, setFiltroProfesionalId] = useState('');
    const [profesionalesActivos, setProfesionalesActivos] = useState([]);

    const [showModalInfo, setShowModalInfo] = useState(false);
    const [showModalAcciones, setShowModalAcciones] = useState(false);
    const [showModalConfigSispro, setShowModalConfigSispro] = useState(false);
    const [ripsSeleccionado, setRipsSeleccionado] = useState(null);
    const [ripsAccion, setRipsAccion] = useState(null);
    const [sisproEnvioItem, setSisproEnvioItem] = useState(null);
    const [enviandoSispro, setEnviandoSispro] = useState(false);
    const [puedeEnviarSispro, setPuedeEnviarSispro] = useState(false);
    const [showModalEditar, setShowModalEditar] = useState(false);
    const [ripsEditando, setRipsEditando] = useState(null);

    useEffect(() => {
        let userdata = {};
        if (localStorage.getItem('userdata')) {
            userdata = JSON.parse(localStorage.getItem('userdata'));
        }

        let tienePermiso = false;
        if (userdata.permissions && Array.isArray(userdata.permissions)) {
            tienePermiso = userdata.permissions.some((p) => p.slug === 'administrarRips');
        } else if (userdata.up && userdata.up.includes('administrarRips')) {
            tienePermiso = true;
        }

        if (!tienePermiso) {
            setHasPermission(false);
            return;
        }

        setHasPermission(true);
        setPuedeEnviarSispro(usuarioPuedeEnviarSispro());

        const hoy = new Date();
        const primerDia = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
        const ultimoDia = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0);

        setFechaInicio(toInputDateFromLocalDate(primerDia));
        setFechaFin(toInputDateFromLocalDate(ultimoDia));

        axios.get('/users-by-client?uid=' + userdata.user_id)
            .then((response) => {
                if (response.status === 200) {
                    setProfesionalesActivos(response.data || []);
                }
            })
            .catch((err) => console.error('Error cargando profesionales:', err));
    }, []);

    useEffect(() => {
        if (fechaInicio && fechaFin) {
            cargarRips();
        }
    }, [fechaInicio, fechaFin, filtroTipo, filtroProfesionalId]);

    const cargarRips = async () => {
        setLoading(true);
        try {
            let userdata = JSON.parse(localStorage.getItem('userdata'));
            const response = await axios.get('/rips', {
                params: {
                    uid: userdata.user_id,
                    fecha_inicio: fechaInicio,
                    fecha_fin: fechaFin,
                    tipo: filtroTipo,
                    profesional_id: filtroProfesionalId || undefined,
                }
            });
            setRips(response.data);
        } catch (error) {
            console.error('Error cargando RIPS:', error);
            alert('Error al cargar los registros RIPS');
        } finally {
            setLoading(false);
        }
    };

    const formatearFecha = (fecha) => formatDateOnly(fecha);

    const getValorListado = (item) => {
        const esElectronica = item.factura_id && item.factura_tipo_factura === 'electronica';
        if (esElectronica && item.factura_valor_total != null && item.factura_valor_total !== '') {
            return parseFloat(item.factura_valor_total);
        }
        return item.valor_rips;
    };

    const formatearValor = (valor) => {
        if (!valor || valor === 0) return '-';
        return '$' + Math.round(parseFloat(valor)).toLocaleString('es-CO');
    };

    const getPacienteDatosDesdeItem = (item) => ({
        tipo_cliente: item.paciente_tipo_cliente,
        primer_nombre: item.paciente_primer_nombre,
        segundo_nombre: item.paciente_segundo_nombre,
        primer_apellido: item.paciente_primer_apellido,
        segundo_apellido: item.paciente_segundo_apellido,
        tipo_documento: item.paciente_tipo_doc,
        numero_documento: item.paciente_numero_doc,
        celular: item.paciente_celular,
        email: item.paciente_email,
        direccion: item.paciente_direccion,
        zona: item.paciente_zona,
        ciudad: item.paciente_ciudad,
        pais_residencia: item.paciente_pais_residencia,
    });

    const pacienteDatosCompletos = (item) =>
        pacienteTieneDatosCompletosParaFactura(getPacienteDatosDesdeItem(item));

    const getTipoRegistro = (rips) => {
        const tipos = [];
        if (rips.consultas) tipos.push('Consulta');
        if (rips.procedimientos) tipos.push('Procedimiento');
        return tipos.join(' + ') || 'N/A';
    };

    /** Misma regla que ListadoFacturas / EstadoCuenta: no duplicar prefijo si ya viene en numero_factura. */
    const getEtiquetaFacturaItem = (item) => getEtiquetaFactura(
        item.factura_prefijo,
        item.factura_numero,
        item.factura_id
    );

    const getEstadoRipsBadge = (item) => {
        const enviadoSispro = item.sispro_enviado === 1
            || item.sispro_enviado === true
            || item.sispro_enviado === '1';

        if (enviadoSispro) {
            return <span className="badge badge-outline-info">Enviado SISPRO</span>;
        }

        const est = parseInt(item.estado, 10);
        const facturaEstado = parseInt(item.factura_estado, 10);
        // FEV no emitida/enviada a DIAN: el RIPS no puede ir a SISPRO → siempre Sin Enviar
        const facturaPendienteDian = item.factura_id
            && [0, 6, 7].includes(facturaEstado);

        if (facturaPendienteDian) {
            return <span className="badge badge-outline-secondary">Sin Enviar</span>;
        }

        switch (est) {
            case 0:
                return <span className="badge badge-outline-danger">Anulado</span>;
            case 1:
                return <span className="badge badge-outline-success">Generado</span>;
            case 2:
                return <span className="badge badge-outline-warning">Por convenio</span>;
            case 3:
                return <span className="badge badge-outline-info">Enviado</span>;
            case 4:
                return <span className="badge badge-outline-info">Enviado convenio</span>;
            default:
                return <span className="badge badge-outline-secondary">Sin Enviar</span>;
        }
    };

    /** Estados según factura: 0 borrador DIAN, 1 contado DIAN, 2 crédito DIAN, 3 anulada, 4–5 no electrónica, 6 error DIAN, 7 borrador RIPS DIAN */
    const getEstadoFacturaBadge = (facturaEstado, facturaId) => {
        if (!facturaId) {
            return <span className="badge badge-outline-secondary">Sin factura</span>;
        }
        const n = facturaEstado === null || facturaEstado === undefined ? NaN : parseInt(facturaEstado, 10);
        const map = {
            0: { text: 'Borrador DIAN', cls: 'badge-outline-warning' },
            1: { text: 'Contado DIAN', cls: 'badge-outline-success' },
            2: { text: 'Crédito DIAN', cls: 'badge-outline-success' },
            3: { text: 'Anulada', cls: 'badge-outline-dark' },
            4: { text: 'Contado (no electrónica)', cls: 'badge-outline-info' },
            5: { text: 'Crédito (no electrónica)', cls: 'badge-outline-info' },
            6: { text: 'Error DIAN', cls: 'badge-outline-danger' },
            7: { text: 'Borrador RIPS DIAN', cls: 'badge-outline-warning' },
        };
        const m = map[n] || { text: 'Desconocido', cls: 'badge-outline-secondary' };
        return <span className={`badge ${m.cls}`}>{m.text}</span>;
    };

    const getTipoRips = (item) => {
        if (item.factura_id) {
            if (item.estado === 2 || item.estado === 4) {
                return 'convenio';
            }
            return 'fev';
        }
        return 'sin_fev';
    };

    const buildRipsDetalle = (item) => ({
        rips_id: item.id,
        factura_id: item.factura_id,
        factura_estado: item.factura_estado,
        factura_tipo_factura: item.factura_tipo_factura,
        factura_prefijo: item.factura_prefijo,
        factura_numero: item.factura_numero,
        factura_etiqueta: getEtiquetaFacturaItem(item),
        users_id: item.users_id || item.profesional_tratante_id,
        tipo: getTipoRips(item),
        estado: item.estado,
        sispro_enviado: item.sispro_enviado,
        sispro_cuv: item.sispro_cuv,
        sispro_fecha_radicacion: item.sispro_fecha_radicacion,
        fecha: item.fecha,
        paciente_id: item.paciente_id,
        paciente_nombre: item.paciente_nombre,
        paciente_tipo_doc: item.paciente_tipo_doc,
        paciente_numero_doc: item.paciente_numero_doc,
        paciente_datos: getPacienteDatosDesdeItem(item),
        profesional_nombre: item.profesional_nombre,
        tipo_registro: getTipoRegistro(item),
        titulo_modal: 'Detalle del registro RIPS',
        subtitulo_modal: 'Consulta y gestión del registro',
    });

    const verDetalle = (item) => {
        setRipsSeleccionado(buildRipsDetalle(item));
        setShowModalInfo(true);
    };

    const abrirMenuAcciones = (item) => {
        setRipsAccion(item);
        setShowModalAcciones(true);
    };

    const cerrarMenuAcciones = () => {
        setShowModalAcciones(false);
        setRipsAccion(null);
    };

    const abrirEditarRips = (item) => {
        setRipsEditando(item);
        setShowModalEditar(true);
        cerrarMenuAcciones();
    };

    const cerrarEditarRips = () => {
        setShowModalEditar(false);
        setRipsEditando(null);
        cargarRips();
    };

    const verDetalleDesdeMenu = () => {
        if (!ripsAccion) return;
        const item = ripsAccion;
        cerrarMenuAcciones();
        verDetalle(item);
    };

    const mostrarEnviarSisproEnMenu = (item) => {
        if (!item) return false;
        const estado = parseInt(item.estado, 10);
        if (estado === 0 || estado === 3 || estado === 4) return false;
        return puedeEnviarSispro && !ripsYaEnviadoSispro(item);
    };

    const puedeAnularRips = (item) => {
        if (!item) return false;
        const estado = parseInt(item.estado, 10);
        if (estado === 0 || estado === 3 || estado === 4) return false;
        return !ripsYaEnviadoSispro(item);
    };

    const anularRipsDesdeMenu = async () => {
        if (!ripsAccion || !puedeAnularRips(ripsAccion)) return;

        const ok = window.confirm(
            '¿Anular este RIPS?\n\n' +
            'Quedará en estado Anulado y ya no figurará como pendiente de envío.\n' +
            'La factura electrónica no se modifica: si aplica, anúlela desde Facturación.'
        );
        if (!ok) return;

        try {
            const response = await axios.post(`/rips/${ripsAccion.id}/anular`, {});
            if (response.data?.success !== false) {
                alert(response.data?.message || 'RIPS anulado correctamente.');
                cerrarMenuAcciones();
                cargarRips();
            } else {
                alert(response.data?.error || response.data?.message || 'No se pudo anular el RIPS.');
            }
        } catch (error) {
            console.error('Error anulando RIPS:', error);
            alert(
                error.response?.data?.error
                || error.response?.data?.message
                || 'No se pudo anular el RIPS.'
            );
        }
    };

    const continuarEnvioSispro = async (item) => {
        if (!item || enviandoSispro) return;

        if (!pacienteDatosCompletos(item)) {
            alert('Faltan datos del paciente para enviar el RIPS. Complete el perfil (celular, email, dirección, país, municipio y zona) antes de enviar a SISPRO.');
            cerrarMenuAcciones();
            verDetalle(item);
            return;
        }

        setEnviandoSispro(true);
        try {
            const resultado = await enviarRipsASispro(item);
            if (resultado.ok) {
                cerrarMenuAcciones();
                cargarRips();
                return;
            }
            if (resultado.reason === 'config') {
                setSisproEnvioItem(item);
                cerrarMenuAcciones();
                setShowModalConfigSispro(true);
            }
        } finally {
            setEnviandoSispro(false);
        }
    };

    const handleEnviarSisproDesdeMenu = () => {
        if (!ripsAccion || enviandoSispro) return;
        continuarEnvioSispro(ripsAccion);
    };

    const handleSisproConfigGuardada = async () => {
        const item = sisproEnvioItem;
        setShowModalConfigSispro(false);
        if (!item) return;

        setEnviandoSispro(true);
        try {
            const resultado = await enviarRipsASispro(item, { omitirConfirmacion: true });
            if (resultado.ok) {
                setSisproEnvioItem(null);
                cargarRips();
            } else if (resultado.reason === 'config') {
                setShowModalConfigSispro(true);
            }
        } finally {
            setEnviandoSispro(false);
        }
    };

    const handleCerrarModalConfigSispro = () => {
        setShowModalConfigSispro(false);
        setSisproEnvioItem(null);
    };

    const handleDescargar = async (ripsId) => {
        try {
            let userdata = JSON.parse(localStorage.getItem('userdata'));

            let endpoint;
            let fileName;

            if (ripsSeleccionado && (ripsSeleccionado.estado === 2 || ripsSeleccionado.estado === 4) && ripsSeleccionado.factura_id) {
                endpoint = `/rips/convenio/${ripsSeleccionado.factura_id}/descargar`;
                fileName = `rips_convenio_${ripsSeleccionado.factura_id}`;
            } else {
                endpoint = `/rips/${ripsId}/descargar`;
                fileName = `rips_${ripsId}`;
            }

            const response = await axios.get(endpoint, {
                params: { uid: userdata.user_id },
                responseType: 'blob'
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;

            const contentType = response.headers['content-type'];
            const finalFileName = contentType.includes('zip')
                ? `${fileName}.zip`
                : `${fileName}.json`;

            link.setAttribute('download', finalFileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error Descargando RIPS:', error);
            alert('No Se Pudo Generar El Archivo RIPS. Por Favor, Intente Descargarlo Nuevamente');
        }
    };

    const handleCerrarModal = () => {
        setShowModalInfo(false);
        setRipsSeleccionado(null);
        cargarRips();
    };

    const pagination = useClientPagination(rips, 25);

    if (hasPermission === false) {
        return <Unauthorized />;
    }

    if (hasPermission !== true) {
        return (
            <div className="hold-transition">
                <Loading />
            </div>
        );
    }

    return (
        <div className="hold-transition listado-rips-page">
            <Header />
            <Aside activelink="indicators" />

            <div className="content-wrapper">
                <div className="content-header">
                    <div className="container-fluid">
                        <div className="row mb-2">
                            <div className="col-sm-12">
                                <ol className="breadcrumb">
                                    <li className="breadcrumb-item"><Link to="/home">Home</Link></li>
                                    <li className="breadcrumb-item"><Link to="/indicadores">Indicadores</Link></li>
                                    <li className="breadcrumb-item active">RIPS</li>
                                </ol>
                            </div>
                        </div>
                        <div className="row mb-2 align-items-center">
                            <div className="col-sm-8">
                                <h1 className="m-0">
                                    <i className="fas fa-notes-medical text-secondary mr-2"></i>
                                    Listado de RIPS
                                </h1>
                            </div>
                            <div className="col-sm-4 text-sm-right">
                                <Link to="/indicadores" className="btn btn-outline-secondary btn-sm">
                                    <i className="fas fa-arrow-left mr-1"></i>
                                    Volver
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>

                <section className="content">
                    <div className="container-fluid">
                        <div className="card report-fin-card mb-3">
                            <div className="card-header">
                                <h3 className="card-title">
                                    <i className="fas fa-filter mr-2"></i>
                                    Filtros de búsqueda
                                </h3>
                            </div>
                            <div className="card-body">
                                <div className="row align-items-end">
                                    <div className="col-md-2">
                                        <div className="form-group mb-md-0">
                                            <label>Fecha inicio</label>
                                            <input
                                                type="date"
                                                className="form-control form-control-sm"
                                                value={fechaInicio}
                                                onChange={(e) => setFechaInicio(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-2">
                                        <div className="form-group mb-md-0">
                                            <label>Fecha fin</label>
                                            <input
                                                type="date"
                                                className="form-control form-control-sm"
                                                value={fechaFin}
                                                onChange={(e) => setFechaFin(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-2">
                                        <div className="form-group mb-md-0">
                                            <label>Tipo de registro</label>
                                            <select
                                                className="form-control form-control-sm"
                                                value={filtroTipo}
                                                onChange={(e) => setFiltroTipo(e.target.value)}
                                            >
                                                <option value="">Todos</option>
                                                <option value="consulta">Consultas</option>
                                                <option value="procedimiento">Procedimientos</option>
                                                <option value="ambos">Consulta + procedimiento</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="form-group mb-md-0">
                                            <label>Profesional</label>
                                            <select
                                                className="form-control form-control-sm"
                                                value={filtroProfesionalId}
                                                onChange={(e) => setFiltroProfesionalId(e.target.value)}
                                            >
                                                <option value="">Todos</option>
                                                {profesionalesActivos.map((p) => (
                                                    <option key={p.id} value={p.id}>{p.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="col-md-2">
                                        <div className="form-group mb-0">
                                            <label className="d-block">&nbsp;</label>
                                            <button
                                                type="button"
                                                className="btn btn-secondary btn-sm btn-block"
                                                onClick={cargarRips}
                                                disabled={loading}
                                            >
                                                <i className="fas fa-search mr-1"></i>
                                                Buscar
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="card report-fin-card">
                            <div className="card-header d-flex align-items-center flex-wrap">
                                <h3 className="card-title mb-0">
                                    <i className="fas fa-list mr-2"></i>
                                    Registros RIPS
                                </h3>
                                <div className="card-tools ml-auto mt-2 mt-sm-0">
                                    <span className="listado-rips-count-badge">
                                        {rips.length} {rips.length === 1 ? 'registro' : 'registros'}
                                    </span>
                                </div>
                            </div>
                            <div className="card-body p-0" style={{ overflowX: 'auto' }}>
                                {loading ? (
                                    <div className="text-center py-5">
                                        <div className="spinner-border text-secondary" role="status">
                                            <span className="sr-only">Cargando...</span>
                                        </div>
                                        <p className="mt-3 mb-0 text-muted small">Cargando registros…</p>
                                    </div>
                                ) : (
                                    <table className="table table-bordered table-striped table-hover mb-0 report-fin-table">
                                        <thead>
                                            <tr>
                                                <th>Fecha</th>
                                                <th>Tipo</th>
                                                <th>Paciente</th>
                                                <th>Profesional</th>
                                                <th>Factura</th>
                                                <th className="text-right">Valor</th>
                                                <th className="text-center">Estado factura</th>
                                                <th className="text-center">Estado RIPS</th>
                                                <th className="text-center">Acciones</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {pagination.paginatedItems.length === 0 ? (
                                                <tr>
                                                    <td colSpan="9" className="text-center py-4 text-muted">
                                                        No se encontraron registros RIPS para el período seleccionado.
                                                    </td>
                                                </tr>
                                            ) : (
                                                pagination.paginatedItems.map((item) => (
                                                    <tr key={item.id}>
                                                        <td>{formatearFecha(item.fecha)}</td>
                                                        <td>{getTipoRegistro(item)}</td>
                                                        <td>
                                                            <div className="listado-rips-paciente d-flex align-items-start">
                                                                <div className="flex-grow-1 min-width-0">
                                                                    <strong>{item.paciente_nombre}</strong>
                                                                    <div className="text-muted small">
                                                                        {item.paciente_tipo_doc} {item.paciente_numero_doc}
                                                                    </div>
                                                                </div>
                                                                <span
                                                                    className={`patient-status-icon ml-2 ${pacienteDatosCompletos(item) ? 'patient-status-icon--ok' : 'patient-status-icon--pending'}`}
                                                                    title={pacienteDatosCompletos(item)
                                                                        ? 'Datos del paciente completos para enviar RIPS'
                                                                        : 'Faltan datos del paciente para enviar RIPS'}
                                                                >
                                                                    <i className="fas fa-user"></i>
                                                                </span>
                                                            </div>
                                                        </td>
                                                        <td><span className="small">{item.profesional_nombre}</span></td>
                                                        <td>
                                                            {item.factura_id ? (
                                                                <span className="badge badge-outline-success">
                                                                    {getEtiquetaFacturaItem(item)}
                                                                </span>
                                                            ) : (
                                                                <span className="badge badge-outline-secondary">Sin factura</span>
                                                            )}
                                                        </td>
                                                        <td className="text-right">
                                                            <strong>{formatearValor(getValorListado(item))}</strong>
                                                        </td>
                                                        <td className="text-center">
                                                            {getEstadoFacturaBadge(item.factura_estado, item.factura_id)}
                                                        </td>
                                                        <td className="text-center">{getEstadoRipsBadge(item)}</td>
                                                        <td className="text-center">
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm rips-icon-btn rips-icon-btn--menu"
                                                                title="Opciones"
                                                                onClick={() => abrirMenuAcciones(item)}
                                                            >
                                                                <i className="fas fa-ellipsis-h"></i>
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                            {!loading && (
                                <ReportPaginationFooter
                                    totalRegistros={pagination.totalRegistros}
                                    indicePrimerRegistro={pagination.indicePrimerRegistro}
                                    indiceUltimoRegistro={pagination.indiceUltimoRegistro}
                                    paginaActual={pagination.paginaActual}
                                    cambiarPagina={pagination.cambiarPagina}
                                    totalPaginas={pagination.totalPaginas}
                                    registrosPorPagina={pagination.registrosPorPagina}
                                    onPageSizeChange={pagination.handlePageSizeChange}
                                    itemLabel="registros RIPS"
                                />
                            )}
                        </div>
                    </div>
                </section>
            </div>

            <Footer />

            <Modal show={showModalAcciones} onHide={cerrarMenuAcciones} centered dialogClassName="rips-actions-modal">
                <Modal.Header className="rips-actions-modal__header">
                    <Modal.Title className="rips-actions-modal__title">
                        <span className="rips-actions-modal__title-icon">
                            <i className="fas fa-notes-medical"></i>
                        </span>
                        Acciones del RIPS
                    </Modal.Title>
                    <button type="button" className="rips-actions-modal__close" onClick={cerrarMenuAcciones}>
                        <i className="fas fa-times"></i>
                    </button>
                </Modal.Header>
                <Modal.Body className="rips-actions-modal__body">
                    {ripsAccion && (
                        <>
                            <div className="rips-actions-modal__summary">
                                <span className="rips-actions-modal__avatar">
                                    {(ripsAccion.paciente_nombre || 'R').trim().charAt(0).toUpperCase()}
                                </span>
                                <div>
                                    <div className="rips-actions-modal__patient-name">{ripsAccion.paciente_nombre || 'Sin paciente'}</div>
                                    <div className="rips-actions-modal__patient-meta">
                                        {formatearFecha(ripsAccion.fecha)}
                                        {ripsAccion.paciente_tipo_doc || ripsAccion.paciente_numero_doc
                                            ? ` · ${[ripsAccion.paciente_tipo_doc, ripsAccion.paciente_numero_doc].filter(Boolean).join(' ')}`
                                            : ''}
                                    </div>
                                    <div className="rips-actions-modal__patient-meta">
                                        {getTipoRegistro(ripsAccion)}
                                        {ripsAccion.factura_id
                                            ? ` · Factura: ${getEtiquetaFacturaItem(ripsAccion)}`
                                            : ' · Sin factura'}
                                    </div>
                                </div>
                            </div>
                            <div className="d-flex flex-column rips-actions-modal__buttons">
                                <button
                                    type="button"
                                    className="btn rips-modal-action rips-modal-action--detail"
                                    onClick={verDetalleDesdeMenu}
                                >
                                    <i className="fas fa-eye mr-2"></i>
                                    Ver detalle
                                </button>
                                {!ripsYaEnviadoSispro(ripsAccion) && parseInt(ripsAccion.estado, 10) !== 0 && (
                                    <button
                                        type="button"
                                        className="btn rips-modal-action rips-modal-action--edit"
                                        onClick={() => abrirEditarRips(ripsAccion)}
                                    >
                                        <i className="fas fa-edit mr-2"></i>
                                        Editar RIPS
                                    </button>
                                )}
                                {ripsAccion.factura_id && (
                                    <Link
                                        to={`/informacion-factura/${ripsAccion.factura_id}`}
                                        className="btn rips-modal-action rips-modal-action--invoice"
                                        onClick={cerrarMenuAcciones}
                                    >
                                        <i className="fas fa-file-invoice mr-2"></i>
                                        Ver factura
                                    </Link>
                                )}
                                {mostrarEnviarSisproEnMenu(ripsAccion) && (
                                    <button
                                        type="button"
                                        className="btn rips-modal-action rips-modal-action--sispro"
                                        onClick={handleEnviarSisproDesdeMenu}
                                        disabled={enviandoSispro}
                                    >
                                        {enviandoSispro ? (
                                            <>
                                                <i className="fas fa-spinner fa-spin mr-2"></i>
                                                Enviando...
                                            </>
                                        ) : (
                                            <>
                                                <i className="fas fa-paper-plane mr-2"></i>
                                                Enviar a SISPRO
                                            </>
                                        )}
                                    </button>
                                )}
                                {puedeAnularRips(ripsAccion) && (
                                    <button
                                        type="button"
                                        className="btn rips-modal-action rips-modal-action--anular"
                                        onClick={anularRipsDesdeMenu}
                                    >
                                        <i className="fas fa-ban mr-2"></i>
                                        Anular RIPS
                                    </button>
                                )}
                            </div>
                        </>
                    )}
                </Modal.Body>
            </Modal>

            <Modal show={showModalEditar} onHide={cerrarEditarRips} size="xl" enforceFocus={false} dialogClassName="rips-edit-modal">
                <Modal.Header closeButton>
                    <Modal.Title>
                        Editar RIPS {ripsEditando?.factura_id ? 'con factura' : 'sin factura'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '80vh', overflowY: 'auto' }}>
                    {ripsEditando && (
                        ripsEditando.factura_id ? (
                            <RegistroRipsFev
                                editRipsId={ripsEditando.id}
                                onClose={cerrarEditarRips}
                            />
                        ) : (
                            <RegistroRipsSinFev
                                editRipsId={ripsEditando.id}
                                onClose={cerrarEditarRips}
                            />
                        )
                    )}
                </Modal.Body>
            </Modal>

            <ModalConfigurarSispro
                show={showModalConfigSispro}
                onClose={handleCerrarModalConfigSispro}
                ripsId={sisproEnvioItem?.id}
                ripsUsersId={sisproEnvioItem?.users_id || sisproEnvioItem?.profesional_tratante_id}
                onGuardado={handleSisproConfigGuardada}
            />

            {ripsSeleccionado && (
                <ModalRipsExito
                    show={showModalInfo}
                    onClose={handleCerrarModal}
                    ripsData={ripsSeleccionado}
                    onDescargar={handleDescargar}
                    onEnvioExitoso={() => cargarRips()}
                />
            )}
            <style>{`
                .listado-rips-page .rips-icon-btn {
                    border: 1px solid transparent;
                    border-radius: 10px;
                    width: 31px;
                    height: 31px;
                    padding: 0;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    transition: all .15s ease;
                }
                .listado-rips-page .rips-icon-btn i {
                    font-size: 12px;
                }
                .listado-rips-page .rips-icon-btn--menu {
                    background: rgba(50, 58, 69, 0.1);
                    border-color: rgba(50, 58, 69, 0.18);
                    color: #2f3945;
                }
                .listado-rips-page .rips-icon-btn--menu:hover {
                    background: rgba(50, 58, 69, 0.16);
                    color: #202830;
                    transform: translateY(-1px);
                }
                .listado-rips-page .patient-status-icon {
                    width: 28px;
                    height: 28px;
                    border-radius: 999px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                    margin-top: 2px;
                }
                .listado-rips-page .patient-status-icon i {
                    font-size: 12px;
                }
                .listado-rips-page .patient-status-icon--ok {
                    background: rgba(40, 167, 69, 0.14);
                    color: #28a745;
                    border: 1px solid rgba(40, 167, 69, 0.28);
                }
                .listado-rips-page .patient-status-icon--pending {
                    background: rgba(255, 193, 7, 0.18);
                    color: #d39e00;
                    border: 1px solid rgba(255, 193, 7, 0.4);
                }
                .rips-actions-modal .modal-content {
                    border: none;
                    border-radius: 14px;
                    box-shadow: 0 18px 40px rgba(20, 33, 48, 0.28);
                    overflow: hidden;
                }
                .rips-actions-modal__header {
                    border-bottom: 1px solid #edf2f7;
                    padding: 12px 14px;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                }
                .rips-actions-modal__title {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-size: 1.1rem;
                    font-weight: 700;
                    color: #2b3440;
                }
                .rips-actions-modal__title-icon {
                    width: 28px;
                    height: 28px;
                    border-radius: 8px;
                    background: rgba(84, 110, 122, 0.14);
                    color: #546e7a;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 0.85rem;
                }
                .rips-actions-modal__close {
                    border: none;
                    background: transparent;
                    color: #8a939b;
                    width: 32px;
                    height: 32px;
                    border-radius: 8px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                }
                .rips-actions-modal__close:hover {
                    background: rgba(50, 58, 69, 0.08);
                    color: #323a45;
                }
                .rips-actions-modal__body {
                    padding: 14px 16px 16px;
                }
                .rips-actions-modal__summary {
                    display: flex;
                    align-items: flex-start;
                    gap: 12px;
                    padding: 12px;
                    margin-bottom: 14px;
                    border-radius: 12px;
                    background: #f8f9fb;
                    border: 1px solid #edf2f7;
                }
                .rips-actions-modal__avatar {
                    width: 40px;
                    height: 40px;
                    border-radius: 50%;
                    background: rgba(84, 110, 122, 0.16);
                    color: #546e7a;
                    font-weight: 700;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }
                .rips-actions-modal__patient-name {
                    font-weight: 700;
                    color: #2b3440;
                    font-size: 0.95rem;
                }
                .rips-actions-modal__patient-meta {
                    font-size: 0.78rem;
                    color: #70808f;
                    margin-top: 2px;
                }
                .rips-actions-modal__buttons {
                    gap: 9px;
                }
                .rips-modal-action {
                    border-radius: 12px;
                    font-weight: 700;
                    text-align: left;
                    border: 1px solid transparent;
                    min-height: 42px;
                    transition: all .16s ease;
                    background: transparent;
                    color: #47525e;
                }
                .rips-modal-action:hover {
                    transform: translateY(-1px);
                    background: rgba(50, 58, 69, 0.07);
                    border-color: rgba(50, 58, 69, 0.08);
                    color: #323a45;
                    text-decoration: none;
                }
                .rips-modal-action--detail i {
                    color: #546e7a;
                    width: 18px;
                    text-align: center;
                }
                .rips-modal-action--edit i {
                    color: #e67e22;
                    width: 18px;
                    text-align: center;
                }
                .rips-modal-action--invoice i {
                    color: #11a0b4;
                    width: 18px;
                    text-align: center;
                }
                .rips-modal-action--sispro i {
                    color: #323a45;
                    width: 18px;
                    text-align: center;
                }
                .rips-modal-action--sispro:hover {
                    color: #252b33;
                }
                .rips-modal-action--anular i {
                    color: #dc3545;
                    width: 18px;
                    text-align: center;
                }
                .rips-modal-action--anular:hover {
                    color: #a71d2a;
                    background: rgba(220, 53, 69, 0.08);
                }
            `}</style>
        </div>
    );
}

export default ListadoRips;
